<template>
  <div>
    <div class="breadcrumb">
      <h4 class="overview-text breadcrumb-first">
        <router-link :to="{ name: 'awsoverview' }" class="breadcrumb-link">
          {{ $t('aws.overviewText') }}
        </router-link>
      </h4>
      <h4 class="overview-text">
        {{ $route.params.subscriptionId }}
      </h4>
    </div>
    <RexSpinV3 :spinning="isLoading">
      <div class="overview-tiles">
        <AWSTile :tiles="tilesData" />
        <AWSAlertTable
          :toggleOptions="alertTableToggleOptions"
          :alertsData="dbByConnections"
          :totalAlertsData="alertTableItems"
          alertType="instance"
        />
      </div>
      <div class="overview-subscription-table">
        <AWSTable
          :placeholder="$t('search-database-id')"
          :title="$t('aws.databaseInstances')"
          :headers="databaseOverviewHeader"
          :data="filteredDatabaseInstanceMatrices"
          @subscriptionClicked="handleSubscriptionClick"
          table-filter-field="databaseId"
        />
      </div>
      <div class="drop-down-section">
        <h4>
          {{ this.$t('aws.selectDatabaseInstance') }}
        </h4>

        <RexSelectV3
          mode="multiple"
          :maxTagTextLength="12"
          :maxTagCount="1"
          v-model:value="selectedDatabaseLabel"
          :options="databaseInstances"
          @onChange="onOptionSelect"
          @onDropdownVisibleChange="onOpenSelectField"
          class="capability-dropdown"
          id="search-select-databaseId"
        >
          <template #default>
            <RexSelectOption
              v-for="obj in databaseInstances"
              :key="obj.key"
              :value="obj.label"
            >
            </RexSelectOption>
          </template>
        </RexSelectV3>
      </div>
      <div class="subscription-graphs">
        <div
          class="chart-element"
          v-for="(chartConfig, index) in getGraphTemplate"
          :key="index"
          :style="{ width: chartConfig.width }"
        >
          <RDSGraph
            :graphConfig="chartConfig"
            :index="index"
            :label="graphLegends"
          />
        </div>
      </div>
    </RexSpinV3>
  </div>
</template>

<script>
import awsTemplates from '@/utils/templates/cloud.aws.charts.template.js';
import cloudTemplate from '@/utils/templates/cloud.template';
import AWSTile from '@/components/Cloud/Dashboard/Components/aws-tiles.vue';
import AWSTable from '@/components/Cloud/Dashboard/Components/aws-table.vue';
import AWSAlertTable from '@/components/Cloud/Dashboard/Components/aws-alert-table.vue';
import RexSelectV3 from '@/rex-components/RexSelectV3.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  databaseOverviewHeader,
  alertTableToggleOptionsSubscriptionPage,
  alertsMapper,
} from '@/components/Cloud/Dashboard/cloud.types';
import RDSGraph from '@/components/Cloud/Dashboard/Components/aws-metrics-graph.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import rdsMySqlCharts from '@/utils/templates/cloudTemplates/aws/rds.mysql.config.js';

export default {
  name: 'SubscriptionIdPage',
  components: {
    AWSTile,
    AWSTable,
    AWSAlertTable,
    RDSGraph,
    RexSelectV3,
    RexSpinV3,
  },
  methods: {
    ...mapActions('cloudState/detail', [
      'fetchAwsServiceInstances',
      'fetchReadMatricDataForInstances',
      'getTopAlertsPerInstances',
    ]),
    ...mapMutations('cloudState/detail', [
      'setCurrentServiceInstances',
      'setCurrentServiceInstance',
      'setCurrentPage',
    ]),

    handleSubscriptionClick(databaseId) {
      const service = this.$route.params.service;
      const subscriptionId = this.$route.params.subscriptionId;
      const serviceId = this.$route.params.serviceId;
      const param = `${service}/${subscriptionId}/${serviceId}/${databaseId}`;
      const instanceId = this.databaseInstances.find(
        (instance) => instance.label === databaseId,
      ).key;
      this.setCurrentServiceInstance([instanceId]);
      this.$router.push({
        name: 'databaseId',
        params: { databaseId: param },
      });
    },
    onOptionSelect(value, options) {
      if (options.length > 5) {
        return;
      }

      this.selectedDatabaseLabel = options.map((option) => option.value);
      this.graphLegends = options
        .map((option) => option.label)
        .slice(0, 5)
        .join(', ');
      this.setCurrentServiceInstance(this.selectedDatabaseLabel);
    },
    async getInitialData(currentValue) {
      try {
        this.isLoading = true;
        const serviceId = this.$route.params.serviceId;

        let databaseCluster = {
          duration: currentValue || this.getDurationTime,
          serviceId,
        };
        const databaseInstance =
          await this.fetchAwsServiceInstances(databaseCluster);

        if (!currentValue) {
          this.databaseInstances = databaseInstance.map((instance) => ({
            label: instance.label,
            value: instance.key,
            key: instance.key,
          }));

          // Limit the initial selection to 5
          this.selectedDatabaseLabel = this.databaseInstances
            .slice(0, 5)
            .map((instance) => instance.key);

          this.setCurrentServiceInstance(this.selectedDatabaseLabel);
          this.graphLegends = this.databaseInstances
            .slice(0, 5)
            .map((instance) => instance.label)
            .join(', ');
        }

        !currentValue &&
          this.setCurrentServiceInstance(
            databaseInstance.map((instance) => instance.key),
          );

        const matrices = await this.fetchReadMatricDataForInstances({
          headers: databaseOverviewHeader(),
          iterateInstance: this.databaseInstances,
          getDurationTime: currentValue || this.getDurationTime,
          serviceId,
        });

        this.databaseInstanceMatrices = matrices;
        this.filteredDatabaseInstanceMatrices = matrices;

        this.dbByConnections = currentValue
          ? this.dbByConnections
          : matrices
              .map((matric) => ({
                instanceId: matric.databaseId,
                connections: matric.rds_database_connections,
              }))
              .sort((a, b) => b.connections - a.connections);

        const topAlertsPerInstances = await this.getTopAlertsPerInstances({
          condition: {
            entity: {
              scope: 'Service',
              serviceName: this.$route.params.serviceId,
            },
          },
          duration: currentValue || this.getDurationTime,
        });

        this.alertTableItems =
          topAlertsPerInstances?.data?.getTopMetricsByAlerts?.values
            ?.map((value) => ({
              metrics: this.$t(
                `aws.alertsTable.${this.getAlertMatricNameMapper[value.metricName]}`,
              ),
              alerts: value.alertCount,
            }))
            .filter((alert) => alert.alerts);
      } catch (error) {
        console.error('Error in fetching metrices:', error);
      } finally {
        this.isLoading = false;
      }
    },
    filteredInstanceMatrices(currentValue) {
      this.tilesData = databaseOverviewHeader().map((header) => {
        const key = header.key.toLowerCase();
        const isAverage =
          key.includes('cpu_utilization') ||
          key.includes('read_iops') ||
          key.includes('write_iops');

        const content = currentValue.reduce(
          (accumulator, currValue) => {
            const valueToAdd = isAverage
              ? currValue[key] / currentValue?.length
              : currValue[key];

            if (typeof accumulator === 'object') {
              if (isAverage) {
                accumulator[key] = (accumulator[key] || 0) + valueToAdd;
              } else {
                accumulator[key] = (accumulator[key] || 0) + currValue[key];
              }
            } else {
              accumulator += parseInt(valueToAdd);
            }

            return accumulator;
          },
          isAverage ? {} : 0,
        );

        return {
          title: header.name,
          content:
            typeof content === 'object'
              ? content[key]?.toFixed(2)
              : content?.toFixed(2),
          tooltip: header.tooltip,
        };
      });
    },
    async watchGetDurationTime(currentValue) {
      this.getInitialData(currentValue);
    },
  },

  mounted() {
    this.setCurrentPage('subscriptionId');
    window.scrollTo(0, 0);
  },
  watch: {
    filteredDatabaseInstanceMatrices: {
      handler: function (currentValue) {
        this.filteredInstanceMatrices(currentValue);
      },
    },

    getDurationTime: {
      handler: async function (currentDurationTime) {
        this.watchGetDurationTime(currentDurationTime);
      },
    },
  },

  async beforeMount() {
    await this.getInitialData();
  },

  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime']),
    getGraphTemplate() {
      return rdsMySqlCharts().lineChartsSubscriptionPage;
    },
    getAlertMatricNameMapper() {
      return alertsMapper;
    },
    alertTableToggleOptions() {
      return alertTableToggleOptionsSubscriptionPage();
    },
    databaseOverviewHeader() {
      return [
        {
          key: 'databaseId',
          name: this.$t('aws.rdsSubscriptionScreen.instancesTable.databaseId'),
        },
        ...databaseOverviewHeader(),
      ];
    },
  },
  data() {
    return {
      databaseInstances: [],
      databaseInstanceMatrices: [],
      filteredDatabaseInstanceMatrices: [],
      databaseDropdowndata: [],
      selectedDatabaseLabel: [],
      graphLegends: '',
      tilesData: [],
      awsTemplates: cloudTemplate,
      alertTableItems: [],
      isLoading: false,
      dbByConnections: [],
    };
  },
};
</script>

<style lang="scss" scoped>
.breadcrumb-text {
  display: flex;
  margin-bottom: 1.25rem;
  color: $color-brand-accent;
}

.chart-container {
  @include flex-center-justify(space-between);
  flex-wrap: wrap;
}

.chart-element {
  margin-bottom: 1.25rem;
}

.subscription-graphs {
  @include flex-center-justify(space-between);
  margin-top: 1.25rem;
}

.breadcrumb-first {
  cursor: pointer;
}

.breadcrumb {
  display: flex;
  background: inherit;
  gap: 0.3125rem;
}

.breadcrumb-link:hover {
  text-decoration: none;
  color: inherit;
  color: $color-brand-accent;
}

.breadcrumb-link {
  color: $color-brand-accent;
  text-decoration: none;
}

.drop-down-section {
  @include flex-center-justify(start);
  gap: 0.625rem;
  margin-top: 0.875rem;
}
</style>
